/**
 * TableHeader Component
 *
 * Renders table column headers with sort indicators and filter popovers.
 * Uses column definitions from the column library.
 * Supports optional column groups with collapsible headers.
 */

import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import FilterPopover, { FilterKey } from "../FilterPopover";
import { ColumnDefinition, ColumnFilters, DataSource, SortConfig } from "./columns/columnTypes";
import { ColumnGroup } from "./configs/types";

/**
 * Data source indicator dot component.
 * - Green: live data (real-time from APIs)
 * - Gray: stored data (persisted from backend)
 * - Yellow: hybrid (stored overrides live when present)
 */
const DataSourceDot: React.FC<{ dataSource?: DataSource }> = ({ dataSource }) => {
  // Default to live if not specified
  const source = dataSource ?? "live";
  
  const colorClass = {
    live: "bg-emerald-500",
    stored: "bg-gray-400",
    hybrid: "bg-amber-400",
  }[source];
  
  const title = {
    live: "Live data (real-time)",
    stored: "Stored data (from backend)",
    hybrid: "Hybrid (stored overrides live)",
  }[source];
  
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full ${colorClass} flex-shrink-0`}
      title={title}
    />
  );
};

/**
 * Legend explaining the data source indicator dots.
 */
const DataSourceLegend: React.FC<{ isDarkMode: boolean }> = ({ isDarkMode }) => {
  const textClass = isDarkMode ? "text-gray-500" : "text-gray-400";
  
  return (
    <div className={`flex items-center gap-3 text-[10px] ${textClass}`}>
      <div className="flex items-center gap-1">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span>Live</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
        <span>Stored</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
        <span>Hybrid</span>
      </div>
    </div>
  );
};

export interface TableHeaderProps {
  columns: ColumnDefinition[];
  sortConfig: SortConfig | null;
  onSort: (columnId: string) => void;
  columnFilters: ColumnFilters;
  onToggleFilter: (columnId: string, value: string) => void;
  onSelectAllFilter: (columnId: string, values: string[]) => void;
  onClearFilter: (columnId: string) => void;
  filterOptions: Record<string, string[]>;
  isDarkMode: boolean;
  /** Optional column groups for grouped headers */
  columnGroups?: ColumnGroup[];
  /** Callback when visible columns change due to group collapse/expand */
  onVisibleColumnsChange?: (visibleColumnIds: string[]) => void;
}

/**
 * Sort icon component
 */
const SortIcon: React.FC<{ columnId: string; sortConfig: SortConfig | null }> = ({
  columnId,
  sortConfig,
}) => {
  if (sortConfig?.column !== columnId) {
    return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  }
  return sortConfig.direction === "asc" ? (
    <ArrowUp className="w-3 h-3" />
  ) : (
    <ArrowDown className="w-3 h-3" />
  );
};

/**
 * Background colors for column groups (dark mode)
 * Simple alternating pattern: shaded / unshaded
 */
const GROUP_COLORS_DARK: Record<string, string> = {
  "symbol-info": "bg-gray-800",
  "current-position": "bg-gray-750",
  "target-position": "bg-gray-800",
  "trade-action": "bg-gray-750",
  "stage-assignment": "bg-gray-800",
  "execution": "bg-gray-750",
};

/**
 * Background colors for column groups (light mode)
 * Simple alternating pattern: shaded / unshaded
 */
const GROUP_COLORS_LIGHT: Record<string, string> = {
  "symbol-info": "bg-gray-100",
  "current-position": "bg-gray-50",
  "target-position": "bg-gray-100",
  "trade-action": "bg-gray-50",
  "stage-assignment": "bg-gray-100",
  "execution": "bg-gray-50",
};

const TableHeader: React.FC<TableHeaderProps> = ({
  columns,
  sortConfig,
  onSort,
  columnFilters,
  onToggleFilter,
  onSelectAllFilter,
  onClearFilter,
  filterOptions,
  isDarkMode,
  columnGroups,
  onVisibleColumnsChange,
}) => {
  // Track which groups are expanded (default from config or true)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    if (!columnGroups) return {};
    const initial: Record<string, boolean> = {};
    columnGroups.forEach(group => {
      initial[group.id] = group.defaultExpanded !== false;
    });
    return initial;
  });

  /**
   * Toggle a group's expanded state
   */
  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const newState = { ...prev, [groupId]: !prev[groupId] };
      
      // Notify parent of visible columns change if callback provided
      if (onVisibleColumnsChange && columnGroups) {
        const visibleIds = columns
          .filter(col => {
            const group = columnGroups.find(g => g.columns.includes(col.id));
            if (!group) return true; // Not in any group, always visible
            return newState[group.id];
          })
          .map(col => col.id);
        onVisibleColumnsChange(visibleIds);
      }
      
      return newState;
    });
  }, [columns, columnGroups, onVisibleColumnsChange]);

  /**
   * Get visible columns based on group expand states
   */
  const visibleColumns = useMemo(() => {
    if (!columnGroups) return columns;
    
    return columns.filter(col => {
      const group = columnGroups.find(g => g.columns.includes(col.id));
      if (!group) return true; // Not in any group, always visible
      return expandedGroups[group.id];
    });
  }, [columns, columnGroups, expandedGroups]);

  /**
   * Build group header cells info
   */
  const groupHeaderCells = useMemo(() => {
    if (!columnGroups) return null;

    const cells: Array<{
      type: "group" | "ungrouped";
      groupId?: string;
      label?: string;
      colSpan: number;
      isExpanded?: boolean;
    }> = [];

    let i = 0;
    while (i < columns.length) {
      const col = columns[i];
      const group = columnGroups.find(g => g.columns.includes(col.id));

      if (group) {
        // Count consecutive columns in this group that are visible
        const groupColumns = columns.filter(c => group.columns.includes(c.id));
        const isExpanded = expandedGroups[group.id];
        
        // FIX: Skip collapsed groups entirely - they have 0 visible columns
        // Adding colSpan:1 for collapsed groups causes mismatch with column row
        if (!isExpanded) {
          i += groupColumns.length;
          continue;
        }
        
        cells.push({
          type: "group",
          groupId: group.id,
          label: group.label,
          colSpan: groupColumns.length,
          isExpanded: true,
        });

        // Skip all columns in this group
        i += groupColumns.length;
      } else {
        // Ungrouped column
        cells.push({
          type: "ungrouped",
          colSpan: 1,
        });
        i++;
      }
    }

    return cells;
  }, [columns, columnGroups, expandedGroups]);

  /**
   * Check if a column has active filters
   */
  const hasActiveFilter = (columnId: string): boolean => {
    return columnFilters[columnId]?.size > 0;
  };

  /**
   * Map column ID to FilterKey type for FilterPopover compatibility
   */
  const toFilterKey = (columnId: string): FilterKey | null => {
    const validKeys: FilterKey[] = [
      "currency", "group", "sector", "secType", 
      "targetWeightPct", "targetQty", "tradeQty", "tradeValue", "rawTargetQty",
      "tradeAction", "stage", "thresholdStatus", "stageStatus", "drift", "stageWindow", "orderId", "orderStatus"
    ];
    return validKeys.includes(columnId as FilterKey) ? (columnId as FilterKey) : null;
  };

  /**
   * Get background color for a column based on its group
   * Disabled: Keep pure black/white background for cleaner look
   */
  const getColumnGroupBg = (_columnId: string): string => {
    return "";
  };

  return (
    <thead>
      {/* Group header row (only if column groups defined) */}
      {/* top-[-1px] compensates for border-collapse causing 1px shift on scroll */}
      {groupHeaderCells && (
        <tr className={`${isDarkMode ? "bg-black" : "bg-white"} sticky top-[-1px] z-20`}>
          {groupHeaderCells.map((cell, idx) => {
            if (cell.type === "ungrouped") {
              // Use first ungrouped cell to show legend
              if (idx === 0) {
                return (
                  <th
                    key={`ungrouped-${idx}`}
                    className={`px-2 py-1 border ${
                      isDarkMode ? "border-gray-600" : "border-gray-300"
                    }`}
                  >
                    <DataSourceLegend isDarkMode={isDarkMode} />
                  </th>
                );
              }
              return (
                <th
                  key={`ungrouped-${idx}`}
                  className={`px-2 py-1 border ${
                    isDarkMode ? "border-gray-600" : "border-gray-300"
                  }`}
                />
              );
            }

            // Removed: Column group colors - using pure black/white
            const bgColor = "";

            // Show legend in the first group cell
            if (idx === 0) {
              return (
                <th
                  key={cell.groupId}
                  colSpan={cell.colSpan}
                  className={`px-2 py-1 text-center text-xs font-semibold uppercase tracking-wider border cursor-pointer transition-colors ${
                    isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"
                  } ${bgColor} hover:opacity-80`}
                  onClick={() => cell.groupId && toggleGroup(cell.groupId)}
                  title={cell.isExpanded ? "Click to collapse" : "Click to expand"}
                >
                  <div className="flex items-center justify-between gap-2">
                    <DataSourceLegend isDarkMode={isDarkMode} />
                    <div className="flex items-center gap-1">
                      {cell.isExpanded ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                      <span>{cell.label}</span>
                      {!cell.isExpanded && (
                        <span className="text-xs opacity-60">(...)</span>
                      )}
                    </div>
                  </div>
                </th>
              );
            }

            return (
              <th
                key={cell.groupId}
                colSpan={cell.colSpan}
                className={`px-2 py-1 text-center text-xs font-semibold uppercase tracking-wider border cursor-pointer transition-colors ${
                  isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"
                } ${bgColor} hover:opacity-80`}
                onClick={() => cell.groupId && toggleGroup(cell.groupId)}
                title={cell.isExpanded ? "Click to collapse" : "Click to expand"}
              >
                <div className="flex items-center justify-center gap-1">
                  {cell.isExpanded ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                  <span>{cell.label}</span>
                  {!cell.isExpanded && (
                    <span className="text-xs opacity-60">(...)</span>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      )}

      {/* Column header row - positioned directly below group header row */}
      {/* top-[24px] = 25px group header height - 1px border compensation */}
      <tr className={`${isDarkMode ? "bg-black" : "bg-white"} ${columnGroups ? "sticky top-[24px] z-20" : "sticky top-[-1px] z-20"}`}>
        {visibleColumns.map((column, index) => {
          const filterKey = toFilterKey(column.id);
          const hasFilter = hasActiveFilter(column.id);
          const isFilterable = column.filterable && filterKey;
          const isSortable = column.sortable !== false;
          const isFirstColumn = index === 0;

          // Base header cell classes
          const baseClasses = `px-2 py-2 font-medium border ${
            isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
          }`;

          // Alignment
          const alignClass =
            column.align === "right"
              ? "text-right"
              : column.align === "center"
              ? "text-center"
              : "text-left";

          // Diff column styling (removed - using neutral styling)
          const diffStyle = "";

          // Active filter highlighting
          const filterHighlight = hasFilter
            ? isDarkMode
              ? "bg-teal-900/30"
              : "bg-teal-50"
            : "";

          // Sortable cursor
          const sortableClass = isSortable
            ? "cursor-pointer hover:text-teal-500 transition-colors"
            : "";

          // Group background color
          const groupBg = getColumnGroupBg(column.id);

          // All headers are sticky top
          // Symbol header (first column) is ALSO sticky left
          const topPosition = columnGroups ? "top-[24px]" : "top-[-1px]";
          const stickyTopClass = `sticky ${topPosition}`;
          
          let stickyClass = `${stickyTopClass} z-20 ${isDarkMode ? "bg-black" : "bg-white"}`;
          
          if (isFirstColumn) {
            // Symbol gets dual sticky (top + left) and higher z-index
            stickyClass = `${stickyTopClass} left-0 z-30 ${isDarkMode ? "bg-black" : "bg-white"}`;
          }

          return (
            <th
              key={column.id}
              className={`${baseClasses} ${alignClass} ${diffStyle} ${filterHighlight} ${groupBg} ${stickyClass}`}
              style={column.width ? { width: column.width, minWidth: column.width } : undefined}
              title={column.tooltip}
            >
              <div
                className={`flex flex-col items-center gap-0.5 ${
                  column.align === "right"
                    ? "items-end"
                    : column.align === "center"
                    ? "items-center"
                    : "items-start"
                }`}
              >
                {/* Row 1: Label with data source dot */}
                <div className="flex items-center gap-1">
                  <DataSourceDot dataSource={column.dataSource} />
                  {isSortable ? (
                    <span className={sortableClass} onClick={() => onSort(column.id)}>
                      {column.label}
                    </span>
                  ) : (
                    <span>{column.label}</span>
                  )}
                </div>

                {/* Row 2: Sort and filter icons */}
                {(isSortable || (isFilterable && (filterOptions[column.id] || column.filterMode === "nonzero"))) && (
                  <div className="flex items-center gap-1">
                    {isSortable && <SortIcon columnId={column.id} sortConfig={sortConfig} />}

                    {isFilterable && (filterOptions[column.id] || column.filterMode === "nonzero") && (
                      <FilterPopover
                        filterKey={filterKey}
                        options={filterOptions[column.id] || ["Non-Zero"]}
                        label={column.label}
                        selectedValues={columnFilters[column.id] || new Set()}
                        isDarkMode={isDarkMode}
                        onToggleValue={(key, value) => onToggleFilter(key, value)}
                        onSelectAll={(key, values) => onSelectAllFilter(key, values)}
                        onClear={(key) => onClearFilter(key)}
                        filterMode={column.filterMode}
                      />
                    )}
                  </div>
                )}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default TableHeader;
